import {
  Badge,
  Button,
  Column,
  Divider,
  MeatballMenu,
  Modal,
  Row,
  Text,
  Tooltip,
  useDesignTokens,
} from "@gradience/ui";
import { IconContainer } from "@gradience/ui/dist/components/icon";
import { isApiError, useApiPost, useApiQuery } from "../../../../lib/api";
import { useSelectedTest } from "../../../../domain/use-latest-test";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useAppForm } from "../../../../components/forms";
import { displayCurrency } from "../../../../lib/display-currency";
import { useStore } from "@tanstack/react-form";
import { ChargedTo } from "./charged-to";
import { useQuery } from "@tanstack/react-query";

export const Registered = () => {
  const selectedTest = useSelectedTest();
  const registration = useApiQuery(
    "/registrations/:testId",
    { testId: selectedTest.data?.id ?? "" },
    { enabled: selectedTest.data?.id !== undefined }
  );

  const navigate = useNavigate({
    from: "/$test-slug/home",
  });
  const search = useSearch({ from: "/$test-slug/home/" });
  const registerAdditionalStudentsMutation = useApiPost(
    "/registrations/:testId/students",
    {
      testId: selectedTest.data?.id ?? "",
    }
  );
  const payments = useApiQuery(
    "/registrations/:testId/payments",
    {
      testId: selectedTest.data?.id ?? "",
    },
    { enabled: selectedTest.data?.id !== undefined }
  );

  const form = useAppForm({
    defaultValues: { numberOfStudents: undefined } as {
      numberOfStudents: number | undefined;
    },
    onSubmit: async ({ value }) => {
      if (!value.numberOfStudents)
        throw new Error("Must enter a number of students");

      await registerAdditionalStudentsMutation.mutateAsync({
        body: {
          numberOfStudents: parseInt(
            value.numberOfStudents as unknown as string
          ),
        },
      });
      registration.refetch();
      payments.refetch();
      navigate({ search: { modal: undefined } });
    },
  });

  const total = useStore(form.store, (form) => {
    return (
      (form.values.numberOfStudents ?? 0) *
      (registration.data?.perStudentAmount ?? 0)
    );
  });

  const designTokens = useDesignTokens();

  if (!registration.data) {
    return null;
  }

  return (
    <Column gap={24}>
      <Row
        gap={16}
        style={{
          alignItems: "flex-start",
        }}
      >
        <IconContainer icon="card-02" />
        <Column style={{ flex: 1 }}>
          <Text textStyle="20px - Bold">Registration</Text>
          <Text textStyle="14px - Medium">
            <Row gap={4}>
              <Badge
                type="success"
                icon="check-01"
                children={`${registration.data.numberOfStudents} Students Registered`}
              />
            </Row>
          </Text>
        </Column>
        <MeatballMenu
          options={[
            {
              label: "Edit payment method",
              value: "edit-payment-method",
            },
            {
              label: "View payments",
              value: "view-payments",
            },
          ]}
          onSelect={(value) => {
            switch (value) {
              case "edit-payment-method":
                navigate({
                  search: {
                    modal: "billing-method" as any,
                  },
                });
                return;
              case "view-payments":
                navigate({
                  search: {
                    modal: "payments" as any,
                  },
                });
                return;
            }
          }}
        />
      </Row>
      <Button
        text="Register Additional Students"
        leadingIcon="plus-01"
        variant="subdued"
        onPress={() => navigate({ search: { modal: "register-additional" } })}
      />
      <Modal
        open={search.modal === "register-additional"}
        close={() => navigate({ search: { modal: undefined } })}
      >
        <form
          style={{ display: "flex", flexDirection: "column", gap: 32 }}
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <Text textStyle="24px - Bold">Register Additional Students</Text>
          <Column gap={8}>
            <Text textStyle="14px - Medium">
              You currently have {registration.data.numberOfStudents} students
              registered for this test.
            </Text>
            <Text textStyle="16px - Semibold">
              Number of Additional Students
            </Text>
            <Text textStyle="14px - Medium">
              Enter the number of additional students who will take the ULE. You
              can add to this number later if necessary.
            </Text>
            <form.AppField
              name="numberOfStudents"
              children={(field) => (
                <field.InputField
                  type="number"
                  placeholder="Number of additional students"
                />
              )}
            />
          </Column>
          <form.Subscribe
            selector={(form) => form.values.numberOfStudents ?? 0}
          >
            {(numberOfStudents) => (
              <Column gap={16}>
                <Column gap={8}>
                  <Row>
                    <Text textStyle="14px - Medium" style={{ flex: 1 }}>
                      Additional Students
                    </Text>
                    <Text textStyle="14px - Medium">{numberOfStudents}</Text>
                  </Row>
                  <Row>
                    <Text textStyle="14px - Medium" style={{ flex: 1 }}>
                      Price per student
                    </Text>
                    <Text textStyle="14px - Medium">
                      {displayCurrency(registration.data.perStudentAmount)}
                    </Text>
                  </Row>
                </Column>
                <Divider />
                <Column gap={8}>
                  <Row>
                    <Text textStyle="16px - Semibold" style={{ flex: 1 }}>
                      Order Total
                    </Text>
                    <Text textStyle="14px - Medium">
                      {displayCurrency(total)}
                    </Text>
                  </Row>
                </Column>
              </Column>
            )}
          </form.Subscribe>
          <ChargedTo />
          <Row gap={16}>
            <form.Subscribe
              selector={({ values, isSubmitting }) =>
                [values.numberOfStudents, isSubmitting] as const
              }
            >
              {([numberOfStudents, isSubmitting]) => (
                <Tooltip
                  content={
                    numberOfStudents
                      ? undefined
                      : "You must register at least one additional student."
                  }
                >
                  <Button
                    disabled={!numberOfStudents || isSubmitting}
                    text={`Pay ${displayCurrency(total)} and Register`}
                    variant="primary"
                    type="submit"
                  />
                </Tooltip>
              )}
            </form.Subscribe>
            <Button
              text="Cancel"
              variant="subdued"
              onPress={() => {
                navigate({
                  search: {
                    modal: undefined,
                  },
                });
              }}
            />
          </Row>
          {registerAdditionalStudentsMutation.isError && (
            <Text
              textStyle="14px - Bold"
              style={{ color: designTokens.colors.text.brand }}
            >
              {isApiError(registerAdditionalStudentsMutation.error)
                ? registerAdditionalStudentsMutation.error.response?.data
                    .errors[0].message
                : "Something went wrong registering."}
            </Text>
          )}
        </form>
      </Modal>
    </Column>
  );
};
